<template>
	<div :class="$style['input-wrapper']">
		<div :class="$style['app-text-field']">
			<input
				v-bind="$attrs"
				class="input"
				:class="[$style['app-text-field__input']]"
				:value="value.value"
				autocomplete="off"
				@focus="inputFocusHandler"
				@blur="inputBlurHandler"
				@input="inputInputHandler"
			/>
			<NotchedOutline
				:class="$style['app-text-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-text-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>

			<FloatingLabel
				:label-text="labelText"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
		</div>
		<div :class="[$style['app-select'], { [$style['app-select--empty']]: !options.length, [$style['app-select--focused']]: isFocused }]">
			<select
				:class="$style['app-select__input']"
				v-bind="$attrs"
				:value="value.type"
				@focus="inputFocusHandler"
				@blur="inputBlurHandler"
				@input="inputSelectHandler"
			>
				<option
					v-for="selectOption in options"
					:key="selectOption.value"
					:value="selectOption.value"
					:disabled="selectOption.disabled"
				>
					{{ selectOption.text }}
				</option>
			</select>
			<NotchedOutline
				:class="$style['app-select__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-select__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
		</div>
	</div>
</template>
<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
export default {
	name: 'BaseTextOptions',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel
	},
	props: {
		value: {
			type: Object,
			required: true
		},
		labelText: {
			type: String,
			default: ''
		},
		options: {
			type: Array,
			required: true
		}
	},
	data: () => ({
		privateValue: null,
		option: null,
		isFocused: false,
		isFloated: false
	}),
	watch: {
		value (val) {
			this.privateValue = val
			if (!this.isFocused) {
				this.isFloated = !!val
			}
		},
		options (_val) {
			this.privateValue = ''
			if (!this.isFocused) {
				this.isFloated = false
			}
		}
	},
	created () {
		this.privateValue = this.value
		if (this.value !== null && this.value.length) {
			this.isFloated = true
		}
	},
	methods: {
		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},
		inputBlurHandler (event) {
			if (!this.isFocused) return
			this.isFocused = false
			this.isFloated = !!this.value.value
			this.$emit('blur', event)
		},
		inputSelectHandler (event) {
			const value = {
				type: event.target.value,
				value: this.value.value
			}
			this.$emit('input', value)
		},
		inputInputHandler (event) {
			let input = event.target.value
			if (this.$attrs.type && this.$attrs.type === 'number') {
				input = input.replace(/\D/g, '')
			}
			if (this.maxLength && (this.privateValue.length > this.maxLength)) {
				input = (input.slice(0, this.maxLength))
			}
			const value = {
				value: input,
				type: this.value.type
			}
			this.$emit('input', value)
		}
	}
}
</script>
<style lang="scss" module>
    .input-wrapper {
        display: flex;
        margin: $gutter * 1.25 0;

        &:not(:focus):hover {
            & .app-text-field__outline,
            .app-select__outline {
                border-color: getColor(text);
            }

            & .app-text-field__idle,
            .app-select__idle {
                border-color: getColor(text);
            }
        }
    }

    .app-text-field {
        position: relative;
        display: inline-flex;
        width: 60%;
        align-items: flex-end;
        box-sizing: border-box;
        height: 48px;
        border: none;

        &__idle {
            border-width: 1px 0 1px 1px;
            border-radius: 4px 0 0 4px;
            background-color: getColor(input-background);
        }

        &__outline{
            background-color: getColor(input-background);
        }

        &__input {
            z-index: 2;
            padding: 10px 20px;
            display: flex;
            box-sizing: content-box;
            border: none;
            border-radius: 0;
            background-color: transparent;
            font-size: 16px;
            line-height: 1.75rem;
            font-weight: 400;
            letter-spacing: .0094em;
            color: getColor(text);
            width: 100%;
            height: 30px;
            transition: opacity .18s cubic-bezier(.4,0,.2,1);
            caret-color: getColor(tertiary);

            &:focus {
                outline: none;
            }

            &:-webkit-autofill,
            &:-webkit-autofill:hover,
            &:-webkit-autofill:focus {
                animation-name: autofill;
                animation-fill-mode: both;
                -webkit-text-fill-color: getColor(text) !important;
            }

            &[type="number"]::-webkit-inner-spin-button,
            &[type="number"]::-webkit-outer-spin-button {
                appearance: none;
                margin: 0;
            }
        }
    }

    .app-select {
        z-index: 1;
        left: -3px;
        position: relative;
        display: inline-flex;
        width: 40%;
        align-items: flex-end;
        box-sizing: border-box;
        height: 48px;
        border: none;

        // Use pseudo-element with mask for theme-adaptive dropdown icon
        &::after {
            content: "";
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);
            width: 10px;
            height: 5px;
            background-color: getColor(grey);
            mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
            pointer-events: none;
            z-index: 3;
            transition: transform 0.2s ease-in-out;
        }

        &__idle{
            border-radius: 0 4px 4px 0;
            background-color: getColor(input-background);
            z-index: -1;
        }

        &__outline{
            background-color: getColor(input-background);
            z-index: -1;
        }

        &__input {
            z-index: 2;
            padding: 12px 26px 10px 16px;
            display: flex;
            border: none;
            background-color: transparent;
            color: getColor(text);
            line-height: 1.75rem;
            font-size: 14px;
            width: 100%;
            outline: none;
            white-space: nowrap;
            cursor: pointer;
            appearance: none;
        }

        &--empty::after {
            display: none;
        }

        &--focused::after {
            transform: translateY(-50%) rotate(-180deg);
        }
    }
</style>
